import { Box, Button, Flex, Text } from '@rocket/ui';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useFtueHost } from '../host';
import { useOnboarding } from '../OnboardingContext';
import type { ExtractedSection, SlotSurfaceForms } from '../onboarding-api';
import { createPlaybook, matchExtractedRules } from '../onboarding-api';
import { OnboardingComplete } from './OnboardingComplete';
import { OnboardingFinalizePlaybook } from './OnboardingFinalizePlaybook';
import { OnboardingFirstPartyAnalysis } from './OnboardingFirstPartyAnalysis';
import { OnboardingPlaybookSetup } from './OnboardingPlaybookSetup';
import { OnboardingReviewPlaybook } from './OnboardingReviewPlaybook';
import { OnboardingReviewRules } from './OnboardingReviewRules';
import { OnboardingUpload } from './OnboardingUpload';
import { OnboardingWelcome } from './OnboardingWelcome';

type PlaybookConfig = {
  playbookId?: string;
  name: string;
  paperType: 'first_party' | 'third_party';
  contractType: string;
  isExisting?: boolean;
};

// Step indices (shared between paths)
// 0: Welcome
// 1: Playbook Setup (new path)
// 2: Review Existing Playbook (existing path) | First Party Analysis (first_party new path)
// 3: Upload
// 4: Review Rules
// 5: Finalize Playbook
// 6: Done

const STEPS_NEW = [
  { title: 'Welcome' },
  { title: 'Playbook' },
  { title: 'Upload' },
  { title: 'Review' },
  { title: 'Finalize' },
  { title: 'Done' },
];

const STEPS_NEW_FIRST_PARTY = [
  { title: 'Welcome' },
  { title: 'Playbook' },
  { title: 'Analyze' },
  { title: 'Upload' },
  { title: 'Review' },
  { title: 'Finalize' },
  { title: 'Done' },
];

const STEPS_EXISTING = [
  { title: 'Welcome' },
  { title: 'Review' },
  { title: 'Upload' },
  { title: 'Rules' },
  { title: 'Finalize' },
  { title: 'Done' },
];

export function OnboardingPage({
  enableChatRuleCreation = true,
}: {
  enableChatRuleCreation?: boolean;
} = {}) {
  const {
    completeOnboarding,
    onboardingStartStep,
    onboardingInitialConfig,
    isReturningUser,
    resumableExtraction,
    autoResumeExtraction,
    acknowledgeAutoResume,
    playbooks,
  } = useOnboarding();
  const { bbAxios, handleUnauthorized, isAuthError, subscription } = useFtueHost();
  const [step, setStep] = useState(onboardingStartStep);
  const [playbookConfig, setPlaybookConfig] = useState<PlaybookConfig | null>(
    onboardingInitialConfig ?? null
  );

  // If a resumable extraction job was found on mount (see OnboardingContext),
  // jump straight to the upload step with just enough config reconstructed
  // from the job's context for OnboardingUpload to render its resume banner.
  // Only do this once, and only if the user hasn't already picked a path
  // (onboardingInitialConfig takes precedence for mid-flow entries).
  useEffect(() => {
    if (!resumableExtraction || onboardingInitialConfig) {
      return;
    }
    const resumedPlaybook = playbooks.find((p) => p._id === resumableExtraction.playbookId);
    setPlaybookConfig({
      playbookId: resumableExtraction.playbookId,
      name: resumedPlaybook?.name ?? 'your playbook',
      paperType: resumableExtraction.paperType ?? 'third_party',
      contractType: resumableExtraction.contractType ?? 'nda',
      isExisting: !!resumedPlaybook,
    });
    setStep(3);
    // Intentionally only re-run if the job identity itself changes -- this is
    // a one-time redirect on discovering a resumable job, not a live sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumableExtraction?.jobId]);
  const [extractedSections, setExtractedSections] = useState<Array<ExtractedSection>>([]);
  const [slotSurfaceForms, setSlotSurfaceForms] = useState<SlotSurfaceForms>({});
  const [isCreatingPlaybook, setIsCreatingPlaybook] = useState(false);
  const [skippedRules, setSkippedRules] = useState<Array<string>>([]);
  const [activeJobId, setActiveJobId] = useState<string | undefined>();
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSearchingMatches, setIsSearchingMatches] = useState(false);

  const isExistingPath = !!playbookConfig?.isExisting;
  const isFirstPartyNew = !isExistingPath && playbookConfig?.paperType === 'first_party';
  // True when the user entered mid-flow (e.g. "Improve playbook" from the playbook card)
  const startedMidFlow = onboardingStartStep > 0;

  const steps = isExistingPath
    ? STEPS_EXISTING
    : isFirstPartyNew
      ? STEPS_NEW_FIRST_PARTY
      : STEPS_NEW;

  const stepIndicatorIndex = (() => {
    if (isExistingPath) {
      const map: Record<number, number> = { 0: 0, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5 };
      return map[step] ?? 0;
    }
    if (isFirstPartyNew) {
      // 0:Welcome 1:Playbook 2:Analyze 3:Upload 4:Review 5:Finalize 6:Done
      const map: Record<number, number> = { 0: 0, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6 };
      return map[step] ?? 0;
    }
    const map: Record<number, number> = { 0: 0, 1: 1, 3: 2, 4: 3, 5: 4, 6: 5 };
    return map[step] ?? 0;
  })();

  // When the user enters mid-flow (e.g. augment from playbook card) the Welcome
  // step is never shown, so strip it from the progress indicator.
  const visibleSteps = startedMidFlow ? steps.slice(1) : steps;
  const visibleStepIndex = startedMidFlow
    ? Math.max(0, stepIndicatorIndex - 1)
    : stepIndicatorIndex;

  const handleSelectExisting = (playbookId: string, playbookName: string) => {
    setPlaybookConfig({
      playbookId,
      name: playbookName,
      paperType: 'third_party',
      contractType: 'nda',
      isExisting: true,
    });
    setStep(2);
  };

  const handleCreateNew = () => {
    setPlaybookConfig(null);
    setStep(1);
  };

  const handlePlaybookConfigured = async (config: {
    name: string;
    paperType: 'first_party' | 'third_party';
    contractType: string;
  }) => {
    // Check entitlements before creating playbook
    const maxPlaybooks = subscription?.entitlements?.maxPlaybooks ?? 0;
    const currentPlaybooks =
      (subscription?.usage?.publishedPlaybooks ?? 0) + (subscription?.usage?.draftPlaybooks ?? 0);

    if (maxPlaybooks > 0 && currentPlaybooks >= maxPlaybooks) {
      toast.error(
        `You've reached your playbook limit (${maxPlaybooks}). Please upgrade your plan to create more playbooks.`,
        {
          toastId: 'playbook-limit-reached',
        }
      );
      return;
    }

    setIsCreatingPlaybook(true);
    try {
      const { playbookId } = await createPlaybook(bbAxios, {
        name: config.name,
        contractType: config.contractType,
      });
      // Immediately publish the newly created playbook so it appears in the 'published' state (Word add‑in only)
      try {
        await bbAxios.post('/api/v2/utility/playbook/publish', { playbookId });
      } catch (publishErr) {
        console.warn('Failed to publish playbook after creation:', publishErr);
      }
      setPlaybookConfig({ ...config, playbookId, isExisting: false });
      // For first party paper: go to the template analysis step (step 2)
      // For third party paper: skip directly to upload (step 3)
      setStep(config.paperType === 'first_party' ? 2 : 3);
    } catch (err) {
      console.error('Failed to create playbook:', err);

      // Don't show generic error for 401 - handleUnauthorized already shows auth message
      if (isAuthError(err)) {
        handleUnauthorized();
        return;
      }

      toast.error('Failed to create playbook. Please try again.', {
        toastId: 'create-playbook-error',
      });
    } finally {
      setIsCreatingPlaybook(false);
    }
  };

  const handleRulesExtracted = (sections: Array<ExtractedSection>, slots?: SlotSurfaceForms) => {
    setExtractedSections(sections);
    if (slots) {
      setSlotSurfaceForms(slots);
    }
    if (sections.length === 0) {
      setSkippedRules([]);
      setStep(5);
    } else {
      setStep(4);

      // Background matching: find similar rules in other playbooks (non-blocking)
      // This runs in the parent so it survives the OnboardingUpload unmount.
      const pid = playbookConfig?.playbookId;
      console.debug(
        '[OnboardingPage.handleRulesExtracted] playbookConfig:',
        playbookConfig,
        'pid:',
        pid
      );
      if (pid) {
        console.debug(
          '[OnboardingPage.handleRulesExtracted] Calling matchExtractedRules with playbookId:',
          pid
        );
        setIsSearchingMatches(true);
        matchExtractedRules(bbAxios, {
          playbookId: pid,
          extractedSections: sections,
          includeSamePlaybook: isExistingPath,
        })
          .then((matchMap) => {
            console.debug(
              '[OnboardingPage] matchExtractedRules returned',
              matchMap.size,
              'entries',
              [...matchMap.entries()].map(([id, m]) => ({ id, count: m.length }))
            );
            if (matchMap.size === 0) {
              return;
            }
            setExtractedSections((prev) => {
              const updated = prev.map((s) => ({
                ...s,
                rules: s.rules.map((r) => ({
                  ...r,
                  suggestedMatches: matchMap.get(r.id) ?? r.suggestedMatches,
                })),
              }));
              const totalMatches = updated.reduce(
                (sum, s) => sum + s.rules.filter((r) => r.suggestedMatches?.length).length,
                0
              );
              console.debug(
                '[OnboardingPage] setExtractedSections: updating',
                totalMatches,
                'rules with matches'
              );
              return updated;
            });
          })
          .catch((err) => {
            console.warn('[OnboardingPage.handleRulesExtracted] Rule matching failed:', err);
          })
          .finally(() => {
            setIsSearchingMatches(false);
          });
      } else {
        console.warn(
          '[OnboardingPage.handleRulesExtracted] SKIPPING matchExtractedRules - playbookId is missing!',
          {
            playbookConfig,
            hasConfig: !!playbookConfig,
            hasPid: !!pid,
          }
        );
      }
    }
  };

  const handleTemplateAnalysisComplete = (
    sections: Array<ExtractedSection>,
    slots?: SlotSurfaceForms
  ) => {
    setExtractedSections(sections);
    if (slots) {
      setSlotSurfaceForms(slots);
    }
    // If analysis found rules, go straight to review so user sees results
    // Otherwise go to upload for manual file entry
    if (sections.length > 0) {
      setStep(4);
    } else {
      setStep(3);
    }
  };

  const handleAugmentExistingPlaybook = () => {
    // Proceed to upload step with the existing playbook ID
    // New rules will be added directly to the selected playbook
    setStep(3);
  };

  const handleReviewComplete = (
    updatedPlaybookId?: string,
    newSkippedRules?: Array<string>,
    jobId?: string
  ) => {
    if (updatedPlaybookId) {
      setPlaybookConfig((prev) => (prev ? { ...prev, playbookId: updatedPlaybookId } : prev));
    }
    setSkippedRules(newSkippedRules ?? []);
    setActiveJobId(jobId);
    setSlotSurfaceForms({});
    setStep(5);
  };

  const handleFinalizeComplete = () => {
    setStep(6);
  };

  return (
    <Box position="fixed" inset="0" zIndex="1500" bg="bg" overflowY="auto">
      <Flex.V minH="100%" gap="0">
        {/* Header - Compact step indicator */}
        <Flex.H
          px="4"
          py="3"
          borderBottom="1px solid"
          borderColor="border"
          justifyContent="center"
          alignItems="center"
          gap="2"
          bg="bg.muted"
        >
          <Text fontSize="sm" fontWeight="medium" color="fg.muted" whiteSpace="nowrap">
            Step {visibleStepIndex + 1} of {visibleSteps.length}:
          </Text>
          <Text fontSize="sm" fontWeight="semibold" color="fg" truncate>
            {visibleSteps[visibleStepIndex]?.title}
          </Text>
        </Flex.H>

        {/* Content */}
        <Box flex="1" display="flex" flexDirection="column" minH="0">
          {step === 0 && (
            <OnboardingWelcome
              onSelectExisting={handleSelectExisting}
              onCreateNew={handleCreateNew}
              isReturningUser={isReturningUser}
            />
          )}
          {step === 1 && (
            <OnboardingPlaybookSetup
              onBack={() => setStep(0)}
              onNext={handlePlaybookConfigured}
              isLoading={isCreatingPlaybook}
              isReturningUser={isReturningUser}
            />
          )}
          {step === 2 && isFirstPartyNew && playbookConfig?.playbookId && (
            <OnboardingFirstPartyAnalysis
              playbookId={playbookConfig.playbookId}
              contractType={playbookConfig.contractType}
              onBack={() => setStep(1)}
              onSkip={() => setStep(3)}
              onRulesExtracted={handleTemplateAnalysisComplete}
              onExtractionStateChange={setIsExtracting}
            />
          )}
          {step === 2 && isExistingPath && playbookConfig?.playbookId && (
            <OnboardingReviewPlaybook
              playbookId={playbookConfig.playbookId}
              playbookName={playbookConfig.name}
              onBack={() => (startedMidFlow ? completeOnboarding() : setStep(0))}
              onAugment={handleAugmentExistingPlaybook}
              onDone={handleReviewComplete}
            />
          )}
          {step === 3 && (
            <OnboardingUpload
              paperType={playbookConfig?.paperType ?? 'third_party'}
              playbookId={playbookConfig?.playbookId}
              contractType={playbookConfig?.contractType || 'nda'}
              initialSections={extractedSections}
              onBack={() => setStep(isExistingPath ? 2 : isFirstPartyNew ? 2 : 1)}
              onRulesExtracted={handleRulesExtracted}
              onExtractionStateChange={setIsExtracting}
              enableChatRuleCreation={enableChatRuleCreation}
              resumableJob={resumableExtraction}
              autoResume={autoResumeExtraction}
              onAutoResumeHandled={acknowledgeAutoResume}
            />
          )}
          {step === 4 && (
            <OnboardingReviewRules
              sections={extractedSections}
              playbookConfig={playbookConfig}
              slotSurfaceForms={slotSurfaceForms}
              isSearchingMatches={isSearchingMatches}
              onBack={() => setStep(3)}
              onComplete={handleReviewComplete}
            />
          )}
          {step === 5 && playbookConfig?.playbookId && (
            <OnboardingFinalizePlaybook
              playbookId={playbookConfig.playbookId}
              playbookName={playbookConfig.name}
              paperType={playbookConfig.paperType}
              contractType={playbookConfig.contractType}
              skippedRules={skippedRules}
              jobId={activeJobId}
              onContinue={handleFinalizeComplete}
              enableChatRuleCreation={enableChatRuleCreation}
            />
          )}
          {step === 6 && (
            <OnboardingComplete
              playbookId={playbookConfig?.playbookId}
              playbookName={playbookConfig?.name}
              onFinish={(playbookId) => completeOnboarding(playbookId)}
            />
          )}
        </Box>

        {/* Skip link */}
        <Flex.H justify="center" py="3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => completeOnboarding()}
            disabled={isExtracting}
          >
            <Text fontSize="xs" color="fg.muted">
              Skip setup
            </Text>
          </Button>
        </Flex.H>
      </Flex.V>
    </Box>
  );
}
